import { Injectable } from '@angular/core';
import { Observable, Subscription, filter, interval, lastValueFrom, timer } from 'rxjs';
import { WebSocketSubject, webSocket } from 'rxjs/webSocket';
import { APP } from 'src/app/config/app';
import { environment } from 'src/environments/environment';
import { PhaseStatus } from 'src/app/models/enums';
import { LiteralService } from '../literal/literal.service';

const WS_NOT_JSON = Symbol('WS_NOT_JSON');

export interface IOperationOutcome {
  result?: any;
  error?: any;
  trackingLost?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket$: WebSocketSubject<any> | undefined;
  private ttlSub: Subscription | undefined;
  private operationCompleted = false;

  constructor(private literalService: LiteralService) {}

  // Phase names arrive as enum keys; the entity being processed is appended when the backend sends one
  progressDetail(message: any): string {
    const phaseKey = message?.phase_name ? `progress.phases.${message.phase_name}` : '';
    const translated = phaseKey ? this.literalService.get(phaseKey) : '';
    const phase = translated && translated !== phaseKey ? this.literalService.get(phaseKey, true) : (message?.phase_name ?? '');
    const entityName = message?.progress_entity_data?.name;
    return entityName ? `${phase}: ${entityName}` : phase;
  }

  // Every phase ends with a COMPLETED message; only the one at 100%, a failure or a plain result end the operation
  isOperationCompleted(message: any): boolean {
    if (!message || typeof message !== 'object') return false;
    if (!('phase_status' in message)) return true;
    return message.phase_status === PhaseStatus.FAILED
      || (message.phase_status === PhaseStatus.COMPLETED && message.total_percentage === 100);
  }

  // A failure comes back instead of thrown: the caller has to tell a refusal it must ask about from a real error
  // Long phases send nothing for a while, so the socket is kept alive; if it is lost anyway it reconnects
  // with a new token and the server replays the latest state of the operation
  trackOperation(
    operationId: string,
    sha: string,
    onProgress?: (message: any) => void,
    renewSha?: () => Promise<string>,
  ): Promise<IOperationOutcome> {
    return new Promise((resolve) => {
      let reconnections = 0;
      let finished = false;
      let keepalive: Subscription | undefined;
      const finish = (outcome: IOperationOutcome) => {
        finished = true;
        keepalive?.unsubscribe();
        resolve(outcome);
      };
      const lost = async (error: any) => {
        if (finished) return;
        keepalive?.unsubscribe();
        this.closeConnection();
        if (!renewSha || reconnections >= APP.WEBSOCKET_RECONNECT_ATTEMPTS) {
          finish({ error });
          return;
        }
        reconnections++;
        await lastValueFrom(timer(APP.WEBSOCKET_RECONNECT_DELAY_MS));
        try {
          follow(await renewSha());
        } catch (renewError) {
          lost(renewError);
        }
      };
      const follow = (token: string) => {
        this.connect(operationId, token);
        keepalive = interval(APP.WEBSOCKET_KEEPALIVE_MS).subscribe(() =>
          this.sendMessage(APP.WEBSOCKET_KEEPALIVE_MESSAGE),
        );
        this.getMessages().subscribe({
          next: (message: any) => {
            onProgress?.(message);
            if (!('phase_status' in message)) {
              finish({ result: message });
              return;
            }
            if (!this.isOperationCompleted(message)) return;
            if (message.phase_status === PhaseStatus.FAILED) {
              finish({ error: this.failureOf(message) });
              return;
            }
            finish({ result: message.progress_entity_data });
          },
          error: (error: any) => lost(error),
          complete: () => {
            if (!this.operationCompleted)
              lost(new Error('WebSocket closed before the operation completed'));
          },
        });
      };
      follow(sha);
    });
  }

  // A failure travels as the error body serialised into the message
  private failureOf(message: any): any {
    const payload = message?.progress_entity_data;
    if (typeof payload !== 'string') return payload;
    try {
      return JSON.parse(payload);
    } catch {
      return undefined;
    }
  }

  isIntermediatePhaseCompletion(message: any): boolean {
    return message?.phase_status === PhaseStatus.COMPLETED && !this.isOperationCompleted(message);
  }

  connect(operationId: string, sha: string, ttlMs: number = 60000): void {
    this.closeConnection();
    this.operationCompleted = false;
    const url = `${environment.webSocketUrl}?operation-id=${encodeURIComponent(operationId)}&sha=${encodeURIComponent(sha)}`;
    this.socket$ = webSocket({
      url,
      deserializer: (e: MessageEvent) => this.deserialize(e),
    });
    // this.ttlSub = timer(ttlMs).subscribe(() => this.closeConnection());
  }

  deserialize(event: MessageEvent): any {
    if (event.data === 'DISCONNECTED') return this.handleServerDisconnection();
    try {
      const message = JSON.parse(event.data);
      if (this.isOperationCompleted(message)) this.operationCompleted = true;
      return message;
    } catch {
      return WS_NOT_JSON;
    }
  }

  // The server disconnects after the final message, so a disconnection before it means the operation was lost
  private handleServerDisconnection(): typeof WS_NOT_JSON {
    const completed = this.operationCompleted;
    this.closeConnection();
    console.info('WebSocket disconnected by server');
    if (!completed) throw new Error('WebSocket disconnected before the operation completed');
    return WS_NOT_JSON;
  }

  sendMessage(message: any): void {
    this.socket$?.next(message);
  }

  getMessages(): Observable<any> {
    return this.socket$!.asObservable().pipe(
      filter((msg) => msg !== WS_NOT_JSON)
    );
  }

  closeConnection(): void {
    this.ttlSub?.unsubscribe();
    this.ttlSub = undefined;
    if (this.socket$) {
      this.socket$.complete();
      this.socket$ = undefined;
    }
  }
}
